import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getInternalSEColor,
  shortenNarrativeLabel,
  buildChartTheme,
  subtleHorizontalSplitLine,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const StackkedChartNarratives = ({
  data,
  renderZeroWhenEmpty = false,
  zeroCategories = ['APJ', 'EMEA', 'NA', 'LATAM'],
  zeroSeriesNames = ['Narrative Runs'],
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasData = !!(data?.xData?.length);
  if (!hasData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = hasData
    ? data
    : {
        xData: zeroCategories,
        series: zeroSeriesNames.map((name) => ({
          name,
          data: new Array(zeroCategories.length).fill(0),
        })),
        total: zeroCategories.map((region) => ({ region, value: 0 })),
      };
  const isAllZero = (normalizedData.series || []).every((item) =>
    (item?.data || []).every((value) => Number(value) === 0)
  );

  const legendData = (normalizedData.series || [])
    .filter((item) => item?.name)
    .map((item) => item.name);

  const options = {
    animation: false,
    grid: theme.gridWithLegend,
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const regionName = params[0]?.axisValue?.trim();
        const regionTotal =
          (normalizedData.total || []).find((item) => item.region === regionName)?.value || 0;
        let tooltip = `<div style="font-weight:600;margin-bottom:6px;">${regionName} — Total ${regionTotal}</div>`;
        params.forEach((item) => {
          tooltip += `<div><span style="color:${item.color}">●</span> ${item.seriesName}: ${item.value}</div>`;
        });
        return tooltip;
      },
    },
    legend: {
      ...theme.legendBase,
      type: 'scroll',
      data: legendData,
      bottom: 0,
      left: 'center',
      icon: 'roundRect',
      itemGap: isLaptopCompact ? 12 : 14,
      formatter: (name) => truncateChartLabel(shortenNarrativeLabel(name), 18),
    },
    xAxis: {
      type: 'category',
      data: normalizedData.xData,
      axisLabel: theme.axisLabel,
    },
    yAxis: {
      type: 'value',
      name: 'Narrative Runs',
      nameTextStyle: theme.axisNameStyle,
      axisLabel: theme.axisLabel,
      min: 0,
      max: isAllZero ? 1 : undefined,
      splitLine: subtleHorizontalSplitLine,
    },
    series: (normalizedData.series || [])
      .filter((item) => item?.name)
      .map((item, index) => ({
        ...item,
        name: item.name,
        barMaxWidth: 56,
        minPointSize: isAllZero ? 2 : 0,
        itemStyle: { color: getInternalSEColor(index) },
      })),
  };

  return (
    <ReactECharts
      option={options}
      style={{ height: `${isLaptopCompact ? 320 : 340}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default StackkedChartNarratives;
